import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { Check, Download, FileUp } from 'lucide-react';
import { useMemo, useRef, useState, type DragEvent } from 'react';
import { ErrorBanner, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GetFromQuickBooks } from '@/components/migration/quickbooks';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import {
  IMPORT_TYPES,
  MAX_IMPORT_ROWS,
  autoMap,
  downloadCsv,
  parseCsv,
  useImportSpec,
  type ImportKind,
  type ImportResult,
  type ImportSpec,
  type ParsedCsv,
} from '@/lib/imports';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings/import/$kind')({
  beforeLoad: ({ params }) => {
    if (!IMPORT_TYPES.some((t) => t.kind === params.kind)) throw notFound();
  },
  component: ImportWizardPage,
});

type Step = 'upload' | 'map' | 'review' | 'done';
const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'upload', label: 'Upload' },
  { key: 'map', label: 'Map columns' },
  { key: 'review', label: 'Review' },
  { key: 'done', label: 'Done' },
];

function ImportWizardPage() {
  const { kind } = Route.useParams() as { kind: ImportKind };
  const type = IMPORT_TYPES.find((t) => t.kind === kind)!;
  const orgId = useOrgId();
  const me = useMe();
  const spec = useImportSpec(kind);
  return (
    <>
      <PageHeader
        title={`Import ${type.title.toLowerCase()}`}
        description="Upload a CSV file, match its columns, review the rows, then import the ones without errors."
        actions={
          <Button variant="outline" asChild>
            <Link to="/settings/import">All imports</Link>
          </Button>
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : me.data && !me.data.isStaff ? (
        <EmptyState
          title="Only accounting staff can import data"
          hint="Ask your accountant to import lists for you."
        />
      ) : spec.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : spec.isError ? (
        <EmptyState title="Could not load the import" hint={spec.error.message} />
      ) : (
        // remount per client so a file chosen for one client never imports into another
        <Wizard key={`${orgId}-${kind}`} spec={spec.data} list={type.list} title={type.title} />
      )}
    </>
  );
}

function Wizard({ spec, list, title }: { spec: ImportSpec; list: string; title: string }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<{ name: string; csv: ParsedCsv } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [onlyErrors, setOnlyErrors] = useState(false);
  const one = title.toLowerCase();

  const rowsPayload = () => {
    if (!file) return [];
    const index = new Map(file.csv.headers.map((h, i) => [h, i]));
    return file.csv.rows.map((r) => {
      const o: Record<string, string> = {};
      for (const [key, header] of Object.entries(mapping)) {
        const i = index.get(header);
        if (i !== undefined) o[key] = (r[i] ?? '').toString();
      }
      return o;
    });
  };

  const preview = useMutation({
    mutationFn: async () => {
      await api(`/orgs/${orgId}/imports/${spec.kind}/mapping`, {
        method: 'PUT',
        body: JSON.stringify({ mapping }),
      });
      return api<ImportResult>(`/orgs/${orgId}/imports/${spec.kind}/preview`, {
        method: 'POST',
        body: JSON.stringify({ rows: rowsPayload() }),
      });
    },
    onSuccess: () => {
      setOnlyErrors(false);
      setStep('review');
      void qc.invalidateQueries({ queryKey: ['import-spec', orgId, spec.kind] });
    },
  });

  const run = useMutation({
    mutationFn: () =>
      api<ImportResult>(`/orgs/${orgId}/imports/${spec.kind}`, {
        method: 'POST',
        body: JSON.stringify({ rows: rowsPayload() }),
      }),
    onSuccess: async () => {
      setStep('done');
      await Promise.all(
        ['accounts', 'contacts', 'items'].map((k) =>
          qc.invalidateQueries({ queryKey: [k, orgId] }),
        ),
      );
    },
  });

  async function choose(f: File | undefined) {
    setFileError(null);
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name) && f.type !== 'text/csv') {
      setFileError('Choose a .csv file. In Excel, use File › Save as › CSV UTF-8.');
      return;
    }
    try {
      const csv = await parseCsv(f);
      if (!csv.rows.length) throw new Error('The file has a header row but no data rows.');
      if (csv.rows.length > MAX_IMPORT_ROWS)
        throw new Error(
          `The file has ${csv.rows.length.toLocaleString('en-GB')} rows; import at most ${MAX_IMPORT_ROWS.toLocaleString('en-GB')} at a time.`,
        );
      setFile({ name: f.name, csv });
      setMapping(autoMap(spec.fields, csv.headers, spec.savedMapping));
      setStep('map');
    } catch (e) {
      setFileError((e as Error).message);
    }
  }

  const result = step === 'done' ? run.data : preview.data;

  function errorReport(r: ImportResult) {
    if (!file) return;
    const labels = new Map(spec.fields.map((f) => [f.key, f.label]));
    const rows = r.rows
      .filter((x) => x.status === 'error')
      .map((x) => [
        ...file.csv.headers.map((_, i) => file.csv.rows[x.row - 1]?.[i] ?? ''),
        x.errors
          .map((e) => (e.field ? `${labels.get(e.field) ?? e.field}: ${e.message}` : e.message))
          .join('; '),
      ]);
    downloadCsv(`${spec.kind}-import-errors.csv`, [[...file.csv.headers, 'Errors'], ...rows]);
  }

  function reset() {
    setFile(null);
    setMapping({});
    preview.reset();
    run.reset();
    setStep('upload');
  }

  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <Stepper step={step} />
      {step === 'upload' ? (
        <UploadStep
          spec={spec}
          error={fileError}
          onFile={choose}
          onSample={() => downloadCsv(`gear-up-ledger-sample-${spec.kind}.csv`, spec.sample)}
        />
      ) : null}
      {step === 'upload' ? <GetFromQuickBooks kind={spec.kind} onFile={choose} /> : null}
      {step === 'map' && file ? (
        <MapStep
          spec={spec}
          file={file}
          mapping={mapping}
          onChange={setMapping}
          error={preview.error?.message ?? null}
          busy={preview.isPending}
          onBack={reset}
          onNext={() => preview.mutate()}
        />
      ) : null}
      {(step === 'review' || step === 'done') && result ? (
        <ReviewStep
          spec={spec}
          result={result}
          done={step === 'done'}
          onlyErrors={onlyErrors}
          setOnlyErrors={setOnlyErrors}
          error={run.error?.message ?? null}
          busy={run.isPending}
          one={one}
          list={list}
          onBack={() => setStep('map')}
          onImport={() => run.mutate()}
          onErrorReport={() => errorReport(result)}
          onAnother={reset}
        />
      ) : null}
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const at = STEPS.findIndex((s) => s.key === step);
  return (
    <ol className="flex items-center gap-2 text-sm" aria-label="Import steps">
      {STEPS.map((s, i) => (
        <li key={s.key} className="flex items-center gap-2">
          <span
            className={cn(
              'flex size-6 items-center justify-center rounded-full border text-xs',
              i < at && 'border-primary bg-primary text-primary-foreground',
              i === at && 'border-primary text-primary',
              i > at && 'text-muted-foreground',
            )}
          >
            {i < at ? <Check className="size-3.5" /> : i + 1}
          </span>
          <span
            className={cn(i === at ? 'font-medium' : 'text-muted-foreground')}
            aria-current={i === at ? 'step' : undefined}
          >
            {s.label}
          </span>
          {i < STEPS.length - 1 ? <span className="mx-1 h-px w-8 bg-border" /> : null}
        </li>
      ))}
    </ol>
  );
}

function UploadStep({
  spec,
  error,
  onFile,
  onSample,
}: {
  spec: ImportSpec;
  error: string | null;
  onFile: (f: File | undefined) => void;
  onSample: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    onFile(e.dataTransfer.files[0]);
  };
  return (
    <>
      <ErrorBanner>{error}</ErrorBanner>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
        className={cn(
          'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed bg-card px-6 py-10 text-center',
          over && 'border-primary bg-primary/5',
        )}
      >
        <FileUp className="size-8 text-primary" />
        <div>
          <p className="font-medium">Drag and drop a CSV file here, or browse</p>
          <p className="text-sm text-muted-foreground">
            Up to {MAX_IMPORT_ROWS.toLocaleString('en-GB')} rows. The first row must hold the column
            names. Excel files: save as CSV UTF-8 first.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => input.current?.click()}>Browse</Button>
          <Button variant="outline" onClick={onSample}>
            <Download className="size-4" /> Download sample file
          </Button>
        </div>
        <input
          ref={input}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          aria-label="CSV file"
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </div>
      <div className="rounded-lg border bg-card">
        <div className="border-b px-4 py-2 text-sm font-semibold">Fields you can import</div>
        <Table>
          <TableBody>
            {spec.fields.map((f) => (
              <TableRow key={f.key}>
                <TableCell className="w-[220px] font-medium">
                  {f.label}
                  {f.required ? ' *' : ''}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{f.hint ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function MapStep({
  spec,
  file,
  mapping,
  onChange,
  error,
  busy,
  onBack,
  onNext,
}: {
  spec: ImportSpec;
  file: { name: string; csv: ParsedCsv };
  mapping: Record<string, string>;
  onChange: (m: Record<string, string>) => void;
  error: string | null;
  busy: boolean;
  onBack: () => void;
  onNext: () => void;
}) {
  const [tried, setTried] = useState(false);
  const first = file.csv.rows[0] ?? [];
  const used = Object.values(mapping);
  const twice = new Set(used.filter((h, i) => used.indexOf(h) !== i));
  const missing = spec.fields.filter((f) => f.required && !mapping[f.key]);
  const unmapped = file.csv.headers.filter((h) => !used.includes(h));
  const blocked = missing.length > 0 || twice.size > 0;
  return (
    <>
      <ErrorBanner>
        {error ??
          (tried && missing.length
            ? `Map a column to ${missing.map((f) => f.label).join(', ')}.`
            : tried && twice.size
              ? `Each column can feed one field: ${[...twice].join(', ')} is mapped twice.`
              : null)}
      </ErrorBanner>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{file.name}</span> ·{' '}
        {file.csv.rows.length.toLocaleString('en-GB')} rows · {file.csv.headers.length} columns. We
        matched what we recognised{spec.savedMapping ? ' and your saved mapping' : ''}; check the
        rest. The mapping is remembered for this client.
      </p>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[240px]">Yedo Books field</TableHead>
              <TableHead className="w-[280px]">Column in your file</TableHead>
              <TableHead>First row</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {spec.fields.map((f) => {
              const header = mapping[f.key] ?? '';
              const i = file.csv.headers.indexOf(header);
              const bad = (tried && f.required && !header) || (header && twice.has(header));
              return (
                <TableRow key={f.key}>
                  <TableCell>
                    <div className="font-medium">
                      {f.label}
                      {f.required ? ' *' : ''}
                    </div>
                    {f.hint ? <div className="text-xs text-muted-foreground">{f.hint}</div> : null}
                  </TableCell>
                  <TableCell>
                    <NativeSelect
                      aria-label={`Column for ${f.label}`}
                      aria-invalid={Boolean(bad)}
                      value={header}
                      onChange={(e) => {
                        const next = { ...mapping };
                        if (e.target.value) next[f.key] = e.target.value;
                        else delete next[f.key];
                        onChange(next);
                      }}
                    >
                      <option value="">Not mapped</option>
                      {file.csv.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </NativeSelect>
                  </TableCell>
                  <TableCell className="max-w-[320px] truncate text-sm text-muted-foreground">
                    {i >= 0 ? (first[i] ?? '') : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {unmapped.length ? (
        <p className="text-xs text-muted-foreground">Not imported: {unmapped.join(', ')}.</p>
      ) : null}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            setTried(true);
            if (!blocked) onNext();
          }}
        >
          {busy ? 'Checking rows…' : 'Next'}
        </Button>
      </div>
    </>
  );
}

function ReviewStep({
  spec,
  result,
  done,
  onlyErrors,
  setOnlyErrors,
  error,
  busy,
  one,
  list,
  onBack,
  onImport,
  onErrorReport,
  onAnother,
}: {
  spec: ImportSpec;
  result: ImportResult;
  done: boolean;
  onlyErrors: boolean;
  setOnlyErrors: (v: boolean) => void;
  error: string | null;
  busy: boolean;
  one: string;
  list: string;
  onBack: () => void;
  onImport: () => void;
  onErrorReport: () => void;
  onAnother: () => void;
}) {
  const labels = useMemo(() => new Map(spec.fields.map((f) => [f.key, f.label])), [spec]);
  const rows = onlyErrors ? result.rows.filter((r) => r.status === 'error') : result.rows;
  return (
    <>
      <ErrorBanner>{error}</ErrorBanner>
      <div
        className={cn(
          'rounded-lg border px-4 py-3 text-sm',
          done ? 'border-primary/30 bg-primary/5' : 'bg-card',
        )}
        role="status"
      >
        {done ? (
          <>
            <span className="font-medium">
              Imported {result.ok.toLocaleString('en-GB')} of {result.total.toLocaleString('en-GB')}{' '}
              rows into {one}.
            </span>{' '}
            {result.errors
              ? `${result.errors.toLocaleString('en-GB')} rows with errors were skipped; download the error report, fix them and import that file.`
              : 'Every row was imported.'}
          </>
        ) : (
          <>
            <span className="font-medium">
              {result.ok.toLocaleString('en-GB')} of {result.total.toLocaleString('en-GB')} rows are
              ready to import.
            </span>{' '}
            {result.errors
              ? `${result.errors.toLocaleString('en-GB')} rows have errors and will be skipped unless you fix the file and upload it again.`
              : 'No errors found.'}
          </>
        )}
      </div>
      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={onlyErrors}
            onChange={(e) => setOnlyErrors(e.target.checked)}
          />
          Show only rows with errors
        </label>
        {result.errors ? (
          <Button variant="outline" size="sm" className="ml-auto" onClick={onErrorReport}>
            <Download className="size-4" /> Download error report
          </Button>
        ) : null}
      </div>
      <div className="max-h-[480px] overflow-y-auto rounded-lg border bg-card">
        <Table>
          <TableHeader className="sticky top-0 bg-card">
            <TableRow>
              <TableHead className="w-[70px] text-right">Line</TableHead>
              <TableHead className="w-[260px]">Name</TableHead>
              <TableHead className="w-[120px]">Status</TableHead>
              <TableHead>Errors</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.row}>
                <TableCell className="tnum text-right text-muted-foreground">{r.row + 1}</TableCell>
                <TableCell className="font-medium">{r.label || '(blank)'}</TableCell>
                <TableCell>
                  {r.status === 'ok' ? (
                    <Badge variant="secondary">{done ? 'Imported' : 'Ready'}</Badge>
                  ) : (
                    <Badge variant="destructive">Error</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {r.errors.map((e, i) => (
                    <div key={i} className="text-destructive">
                      {e.field ? (
                        <span className="font-medium">{labels.get(e.field) ?? e.field}: </span>
                      ) : null}
                      {e.message}
                    </div>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between">
        {done ? (
          <>
            <Button variant="outline" onClick={onAnother}>
              Import another file
            </Button>
            <Button asChild>
              <Link to={list}>View {one}</Link>
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button disabled={busy || result.ok === 0} onClick={onImport}>
              {busy
                ? 'Importing…'
                : `Import ${result.ok.toLocaleString('en-GB')} ${result.ok === 1 ? 'row' : 'rows'}`}
            </Button>
          </>
        )}
      </div>
    </>
  );
}
